import { isStaleChunkError } from '@shared/utils/chunkError';
import { recoverFromStaleChunk } from '@shared/utils/chunkUpdateRecovery';
import { shouldUseServiceWorker } from '@shared/utils/serviceWorker';
import { Component, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary fallback for errors that escaped the global chunk recovery listener.
 */
export class ChunkErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Application error caught by boundary:', error, errorInfo);
    if (isStaleChunkError(error)) void recoverFromStaleChunk();
  }

  render() {
    if (this.state.hasError) {
      if (shouldUseServiceWorker() && this.state.error && isStaleChunkError(this.state.error)) {
        return (
          <div className="flex min-h-screen items-center justify-center bg-base-100" role="status">
            <span className="loading loading-spinner loading-lg text-primary" />
          </div>
        );
      }

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: '#f5f5f5',
          }}
        >
          <div
            style={{
              maxWidth: '400px',
              padding: '30px',
              backgroundColor: 'white',
              borderRadius: '10px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              textAlign: 'center',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            }}
          >
            <h1 style={{ color: '#333', marginBottom: '20px' }}>Application Error</h1>
            <p style={{ color: '#666', lineHeight: '1.5', marginBottom: '20px' }}>
              This page could not load. Refresh the tab or return to the previous page.
            </p>
            <button className="btn" onClick={() => window.location.reload()} type="button">
              Refresh
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
